class ProductCard extends HTMLElement {
  static get observedAttributes () {
    return ['prop-data']
  }

  constructor () {
    super()
    this.elements = {}
    this.data = {}
    this.extraData = {}
  }

  async connectedCallback () {
    this.initializeElements()

    this.setEventListeners()

    const propData = this.getAttribute('data-prop-data')
    const isWishlistCard = this.closest('[data-wishlist-grid-el="grid"]')

    if (propData) {
      this.data = JSON.parse(propData)

      this.renderInitial()
      this.extraData = await this.fetchExtraData(this.data.id)

      if (this.extraData) {
        this.additionalRender()
      }
    } else if (isWishlistCard != null) {
      this.renderSwatches(true)
    }
  }

  setEventListeners () {
    this.addEventListener('product-card:lazy-load-slides', (e) => {
      if (this.getAttribute('data-lazy-loaded') === 'true') return
      this.lazyLoadSlides()
    })
  }

  /**
   * Swaps the first element (index 0) with an element at a specific index.
   * @param {Array} arr - The array to modify.
   * @param {number} index - The index to swap with the first element.
   * @returns {Array} - The modified array.
   */
  swapWithFirst (arr, index) {
    // 1. Validation: Ensure the array exists and the index is within bounds
    if (!arr || index < 0 || index >= arr.length) {
      console.error('Invalid index or array')
      return arr
    }

    // 2. Perform the swap using destructuring
    [arr[0], arr[index]] = [arr[index], arr[0]]

    return arr
  }

  lazyLoadSlides () {
    const imageUrls = this.getAttribute('data-img-urls')
    const imageUrlsArr = imageUrls ? JSON.parse(imageUrls) : []

    if (imageUrlsArr.length <= 2) return

    const lazyImages = imageUrlsArr.slice(2)
    const { imageWrapper } = this.elements

    this.setAttribute('data-lazy-loaded', 'true')

    lazyImages.forEach((src) => {
      imageWrapper.insertAdjacentHTML('beforeend', this.imageTemplate(this.data, src, false))
    })
  }

  initializeElements () {
    this.elements = {
      imageWrapper: this.querySelector('[data-product-card-el="images"]'),
      heading: this.querySelector('[data-product-card-el="heading"]'),
      price: this.querySelector('[data-product-card-el="price"]'),
      salePrice: this.querySelector('[data-product-card-el="salePrice"]'),
      regularPrice: this.querySelector('[data-product-card-el="regularPrice"]'),
      percentage: this.querySelector('[data-product-card-el="percentage"]'),
      wishlist: this.querySelector('[data-product-card-el="wishlist"]'),
      quickAdd: this.querySelectorAll('[data-product-card-el="quick-add"]'),
      link: this.querySelector('[data-product-card-el="link"]'),
      slider: this.querySelector('[data-product-card-el="slider"]'),
      variants: this.querySelector('[data-product-card-el="variants"]'),
      singleVariant: this.querySelector('[data-product-card-el="single-variant"]'),
      swatchesInjection: this.querySelector('[data-product-card-el="swatches-injection"]'),
      tag: this.querySelector('[data-product-card-el="tag"]'),
    }
  }

  renderInitial () {
    const { imageWrapper, heading, wishlist, quickAdd, link, variants, swatchesInjection, slider, tag } = this.elements

    let hasAdditionalImages = false

    if (imageWrapper) {
      let lazyload = false

      if (this.index >= 4) {
        lazyload = true
      }

      let images = this.data.images || []

      let initalImageIndex = document.querySelector('section.category-page')?.getAttribute('data-category-product-card-inital-index')

      if (images.length > 0) {
        if (initalImageIndex) {
          initalImageIndex = parseInt(initalImageIndex, 10)
          images = this.swapWithFirst(images, initalImageIndex)
        }

        imageWrapper.insertAdjacentHTML('beforeend', this.imageTemplate(this.data, images[0].src, lazyload))

        hasAdditionalImages = images?.length > 1

        if (hasAdditionalImages) {
          imageWrapper.insertAdjacentHTML('beforeend', this.imageTemplate(this.data, images[1].src, true))
        }

        this.setAttribute('data-img-urls', JSON.stringify(images.map(image => image.src)))
      }
    }

    if (slider && hasAdditionalImages) {
      slider.setAttribute('data-initialized', 'true')
    }

    if (heading) {
      heading.textContent = this.data.title
    }

    if (wishlist) {
      wishlist.setAttribute('data-product-id', this.data.id)
      wishlist.setAttribute('data-variant-id', this.data.variants[0].id)
      wishlist.setAttribute('data-product-url', `${window.location.origin}/products/${this.data.handle}`)
      wishlist.setAttribute('data-product-title', this.data.title)
      wishlist.setAttribute('data-product-price', this.data.price)
      wishlist.parentElement.setAttribute('data-initialized', 'true')
    }

    if (quickAdd) {
      quickAdd.forEach(el => {
        el.setAttribute('data-product-handle', this.handle)
        el.setAttribute('data-initialized', 'true')
      })
    }

    if (link) {
      const cleaned = this.url.startsWith('/') ? this.url.slice(1) : this.url
      const marketUrl = window.location.origin + window.Shopify.routes.root + cleaned

      link.href = marketUrl
    }

    if (this.data?.is_pickup_only === true) {
      const template = `
        <modal-opener class="no-js-hidden modal-opener__only-available-in-store" data-modal="#PopupModal-only-available-in-store">
          <button type="submit" name="add" data-id="only-available-in-store" class="button--secondary button button--full-width" aria-haspopup="dialog">
            AVAILABLE IN STORE
          </button>
        </modal-opener>
       `
      
      this.elements.quickAdd[0].parentNode.insertAdjacentHTML('beforeend', template)
    } else {
      if (variants) {
        this.renderVariants()

        quickAdd.forEach(el => {
          el.setAttribute('data-initialized', 'true')
        })
      }

      if (swatchesInjection) {
        this.renderSwatches()
      }
    }

    if (tag) {
      this.data.tags.forEach((tagText) => {
        if (tagText.includes('TextLabel:')) {
          tag.textContent = tagText.replace('TextLabel:', '')
        }
      })
    }

    this.setAttribute('data-root', this.data.root)
  }

  renderVariants () {
    const { variants, singleVariant } = this.elements

    const url = `${window.location.origin}/products/${this.data.handle}?variant=${this.data.variants[0].id}`

    if (this.data.variants.length === 1) {
      const singleVariantData = this.data.variants[0]

      let template = ``

      if (singleVariantData?.inventoryLevel > 0 || singleVariantData.available) {
        template = `
          <button data-product-card-el="single-variant" data-product-card-quick-add-el="trigger" data-id="${singleVariantData.id}" class="button product-card-quick-add__trigger-single">
            Add to Bag
          </button>
        `
      } else {
        template = `
          <a href="${url}?variant=${singleVariantData.id}" class="button product-card-quick-add__trigger-single product-card-quick-add__trigger-single--oss">
            Notify me when available
          </a>
        `
      }

      variants.insertAdjacentHTML('afterend', template)

      if (variants) {
        variants.remove()
      }
    } else {
      this.data.variants.forEach(variant => {
        let template = ``

        if (variant.available) {
          template =
          `<li class="product-card-quick-add__listitem">
            <button class="product-card-quick-add__trigger" data-product-card-quick-add-el="trigger" data-id="${variant.id}">
              ${variant.title}
            </button>
          </li>`
        } else {
          template =
          `<li class="product-card-quick-add__listitem">
            <bis-notification
              data-bis-notification-image="${this.data.featured_image.src}"
              data-bis-notification-name="${this.data.title}"
              data-bis-notification-variant="${variant.title}"
              data-bis-notification-price="${window.BAO.Utils.formatMoney(variant.price * 100)}"
              data-bis-notification-id="${variant.id}"
            >
              <button type="button" class="product-card-quick-add__trigger product-card-quick-add__trigger--oss" data-bis-notification-el="trigger" aria-haspopup="dialog">
                ${variant.title}
              </button>
            </bis-notification>
          </li>`
        }

        variants.insertAdjacentHTML('beforeend', template)

        if (variants) {
          singleVariant.remove()
        }
      })
    }
  }

  async renderSwatches (isWishlist) {
    const sectionId = 'product-swatch'
    const marketUrl = window.location.origin + window.Shopify.routes.root

    let productHandle = ''

    if (isWishlist) {
      productHandle = this.dataset.productHandle
    } else {
      productHandle = this.handle
    }

    try {
      const response = await fetch(`${marketUrl}/products/${productHandle}?section_id=${sectionId}`)
      if (!response.ok) throw new Error('Failed to fetch swatches section')
      const text = await response.text()
      const html = new DOMParser().parseFromString(text, 'text/html')

      const swatchesHtml = html.querySelector(`#shopify-section-${sectionId}`).innerHTML
      this.elements.swatchesInjection.innerHTML = swatchesHtml

      const swatchesLoadedEvent = new CustomEvent('GA_SWATCHES_LOADED', {
        detail: {
          swatchesEl: this.elements.swatchesInjection,
        },
      })
      window.dispatchEvent(swatchesLoadedEvent)
    } catch (error) {
      console.error('Failed to fetch swatches section:', error)
    }
  }

  renderPriceFromData () {
    let preorderLabel = ''

    if (this.extraData.preorderEnabled) {
      if (this.extraData.preorderEnabled.value === 'true') {
        preorderLabel = `<span class="product-card__preorder">${this.elements.price.dataset.preorderLabel}</span>`
      }
    }

    const price = this.data.price
    const compareAtPriceMax = this.data.compare_at_price_max || this.data.compare_at_price
    const isOnSale = compareAtPriceMax && compareAtPriceMax > price

    const formattedPrice = window.BAO.Utils.formatMoney(price * 100)

    if (isOnSale) {
      const formattedComparePrice = window.BAO.Utils.formatMoney(compareAtPriceMax * 100)
      const discountPercent = Math.floor(((compareAtPriceMax - price) / compareAtPriceMax) * 100)

      this.elements.price.innerHTML = ''
      this.elements.regularPrice.innerHTML = formattedComparePrice
      this.elements.salePrice.innerHTML = `${formattedPrice} -`
      this.elements.percentage.innerHTML = `${discountPercent}% off`
    } else {
      this.elements.price.innerHTML = `${preorderLabel} ${formattedPrice}`
      this.elements.regularPrice.innerHTML = ''
      this.elements.salePrice.innerHTML = ''
      this.elements.percentage.innerHTML = ''
    }
  }

  async renderQuickbuy () {
    const sectionId = 'product-card-quick-add'

    const productHandle = this.handle

    try {
      const response = await fetch(`/products/${productHandle}?section_id=${sectionId}`)
      if (!response.ok) throw new Error('Failed to fetch swatches section')
      const text = await response.text()
      const html = new DOMParser().parseFromString(text, 'text/html')
      const quickaddHtml = html.querySelector('product-card-quick-add')

      this.elements.quickAdd[0].parentNode.appendChild(quickaddHtml)
      this.elements.quickAdd[0].remove()
      this.elements.quickAdd = this.querySelectorAll('[data-product-card-el="quick-add"]')
    } catch (error) {
      console.error('Failed to update quick adds:', error)
    }
  }

  generateSrcset (url, sizes = [360, 720, 1080]) {
    const [baseUrl, queryParams] = url.split('?')
    const baseWithoutExtension = baseUrl.replace('.jpg', '')
    return sizes.map(size => `${baseWithoutExtension}_${size}x.jpg${queryParams ? `?${queryParams}` : ''} ${size}w`).join(', ')
  }

  imageTemplate (product, image, lazy) {
    const srcset = this.generateSrcset(image)
    return `
      <li data-product-card-slider-el="slide" data-ga-el="imageSlide" data-ga-variable-product="${product.name}">
        <img class="product-card__image" ${lazy === true ? 'loading="lazy"' : 'loading="eager"'} src="${image}" srcset="${srcset}" alt="${product.name}" width="1020" height="680" sizes="(max-width: 767px) 50vw, (min-width: 768px) 25vw">
      </li>
    `
  }

  additionalRender () {
    this.renderPriceFromData()

    if (this.extraData.pantsLengths) {
      this.renderQuickbuy()
    }
  }

  async fetchExtraData (productId) {
    const endpoint = `https://${window.Shopify.shop}/api/2024-04/graphql.json`

    let accessToken

    switch (window.Shopify.shop) {
      case 'victoria-beckham-uk.myshopify.com':
        accessToken = '168f99de7d723da47c230f9c10cf24cb'
        break
      case 'int-staging.myshopify.com':
        accessToken = '194b51b71133ad82470e09e25f65768e'
        break
      default:
        console.log('No access token found')
    }

    const graphqlQuery = `
      query GetProduct($id: ID!) {
        product(id: $id) {
          id
          title
          tags
          variants(first: 10) {
            nodes {
              id
              title
            }
          }
          media(first: 6) {
            edges {
              node {
                id
                alt
                mediaContentType
                previewImage {
                  url
                }
              }
            }
          }
          preorderEnabled: metafield(namespace: "preorder", key: "enable") {
            value
            type
          }
          pantsLengths: metafield(namespace: "shopify", key: "pants-length-type") {
            references(first: 10) {
              nodes {
                ... on Metaobject {
                  id
                  type
                  fields {
                    key
                    value
                  }
                }
              }
            }
          }
        }
      }
    `

    try {
      const variables = {
        id: `gid://shopify/Product/${productId}`,
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken,
        },
        body: JSON.stringify({ query: graphqlQuery, variables }),
      })

      if (!response.ok) throw new Error('Failed to fetch product data')
      const data = await response.json()

      return data.data.product || {}
    } catch (error) {
      console.error('Error fetching extra data:', error)
      return {} // Return empty object as fallback
    }
  }

  get handle () {
    return this.data.handle
  }

  get url () {
    return `/products/${this.data.handle}`
  }

  get index () {
    return Number(this.dataset.index)
  }
}

customElements.define('product-card', ProductCard)
